/** Clasificación de registros para administración por dominio (solo frontend). */
import { isFitLiveUiEnabled } from './fitLiveModule';

export const PRIVILEGED_ADMIN_ROLES = new Set([
  'super_admin',
  'admin_marca',
  'admin_gym_virtual',
  'admin_gimnasio',
  'admin_gym',
  'admin_sede',
  'admin_d28d',
  'admin_food_plan',
  'admin_food',
  'admin_training',
  'admin_entrenador',
]);

export const COACH_STAFF_ROLES = new Set(['entrenador', 'nutricionista']);

export const D28D_STAFF_ROLES = new Set(['entrenador_d28d', 'admin_d28d']);

export function rolesOfRecord(record) {
  if (!record) return [];
  return Array.isArray(record.roles) && record.roles.length
    ? record.roles
    : [record.rol].filter(Boolean);
}

export function hasAnyRoleIn(record, roleSet) {
  return rolesOfRecord(record).some((r) => roleSet.has(r));
}

/** Usuario final sin vínculo a dominio D28D, Training ni FOOD. */
export function isEcosystemFinalUser(user) {
  if (hasAnyRoleIn(user, PRIVILEGED_ADMIN_ROLES)) return false;
  if (hasAnyRoleIn(user, COACH_STAFF_ROLES)) return false;
  if (hasAnyRoleIn(user, D28D_STAFF_ROLES)) return false;
  const roles = rolesOfRecord(user);
  if (!roles.includes('usuario_final') && roles.length > 0) return false;
  if (user.gym_id || user.gymId) return false;
  if (user.trainer_id) return false;
  const ma = user.module_access || {};
  if (ma.d28d || ma.training || ma.food_plan || ma.nutrition) return false;
  return true;
}

/** Usuarios del dominio D28D (gimnasio, programa o staff D28D). */
const D28D_LICENSE_CODES = new Set(['d28d', 'gym', 'live_classes']);

export function isFoodCommercialPlan(planOrUser) {
  if (!planOrUser) return false;
  const directName = String(
    planOrUser.nombre
    || planOrUser.name
    || planOrUser.plan
    || planOrUser.planId
    || '',
  ).trim();
  if (/^FOOD/i.test(directName)) return true;
  const kind = String(
    planOrUser.kind
    || planOrUser.plan_kind
    || '',
  ).toLowerCase();
  if (kind === 'food') return true;
  const subPlan = String(planOrUser.subscription?.plan || '').trim();
  return /^FOOD/i.test(subPlan);
}

export function foodOnlyModuleAccess() {
  return {
    gym: false,
    d28d: false,
    training: false,
    nutrition: true,
    food_plan: true,
    live_classes: false,
  };
}

/** Super admin puede sumar D28D a un usuario Food (híbrido). */
export function wantsD28dProductAccess(moduleAccess = {}) {
  const ma = moduleAccess && typeof moduleAccess === 'object' ? moduleAccess : {};
  return Boolean(ma.d28d || ma.live_classes || ma.gym);
}

/** Plan D28D de plataforma (sin gym/sede) para addon híbrido Food+D28D. */
export function isPlatformD28dAddonPlan(plan) {
  if (!plan || plan.activo === false || plan.visible === false) return false;
  if (plan.for_sedes === true) return false;
  const gymId = plan.gym_id ?? plan.gymId;
  if (gymId != null && gymId !== '') return false;
  const kind = String(plan.kind || '').toLowerCase();
  const name = String(plan.nombre || plan.name || '');
  if (/^FOOD/i.test(name) || /^PLAN SEDE/i.test(name) || kind === 'food' || kind === 'training') return false;
  return kind === 'd28d' || kind === '' || kind === 'gym';
}

/** Food-only (plan/licencia): no listar en Admin D28D aunque gym_id esté contaminado. */
export function isFoodOnlyDomainUser(user) {
  if (!user) return false;
  if (hasAnyRoleIn(user, D28D_STAFF_ROLES)) return false;
  const trainerId = user.trainer_id ?? user.trainerId;
  if (trainerId != null && String(trainerId).trim() !== '') return false;

  const licenses = Array.isArray(user.licenses) ? user.licenses.filter((l) => l.active !== false) : [];
  const codes = new Set(licenses.map((l) => String(l.module_code || '').toLowerCase()));
  const kind = String(user.subscription?.kind || user.subscription?.plan_kind || user.plan_kind || '').toLowerCase();
  const plan = String(user.subscription?.plan || user.planId || user.plan || '').trim().toUpperCase();
  const foodNamed = kind === 'food' || plan === 'FOOD_PLAN' || plan.startsWith('FOOD');
  if (user.subscription?.has_d28d_product === true || user.has_d28d_product === true) return false;
  if (foodNamed && !codes.has('training')) return true;
  if ([...codes].some((c) => D28D_LICENSE_CODES.has(c))) return false;
  if (codes.size > 0 && [...codes].every((c) => c === 'food')) return true;
  return false;
}

function isGymBound(user) {
  const gid = user?.gym_id ?? user?.gymId;
  return gid != null && gid !== '';
}

/** Cliente Training = tiene coach. Independiente de D28D (sin leer kind/has_d28d_product). */
function isTrainingOwnedConsumer(user) {
  if (!user) return false;
  const trainerId = user.trainer_id ?? user.trainerId;
  return trainerId != null && String(trainerId).trim() !== '';
}

/** Licencia D28D real en BD (module_access.d28d suele estar contaminado — no cuenta). */
function hasRealD28dLicense(user) {
  const licenses = Array.isArray(user?.licenses) ? user.licenses.filter((l) => l.active !== false) : [];
  const codes = new Set(licenses.map((l) => String(l.module_code || '').toLowerCase()));
  return [...codes].some((c) => D28D_LICENSE_CODES.has(c));
}

export function isD28dDomainUser(user) {
  if (isFoodOnlyDomainUser(user)) return false;
  // Cliente Training sin licencia D28D real no contamina Admin D28D (ni con gym_id ancla).
  if (isTrainingOwnedConsumer(user) && !hasRealD28dLicense(user)) return false;
  const roles = rolesOfRecord(user);
  if (roles.includes('super_admin')) return false;
  if (roles.some((r) => ['admin_food_plan', 'admin_food', 'admin_training', 'admin_entrenador'].includes(r))) {
    const d28dStaff = hasAnyRoleIn(user, D28D_STAFF_ROLES);
    if (!isGymBound(user) && !d28dStaff) return false;
  }
  if (hasAnyRoleIn(user, COACH_STAFF_ROLES) && !hasAnyRoleIn(user, D28D_STAFF_ROLES)) {
    return false;
  }
  if (hasAnyRoleIn(user, D28D_STAFF_ROLES)) return true;
  if (isGymBound(user)) return true;
  if (user.module_access?.d28d || user.module_access?.gym || user.module_access?.live_classes) return true;
  return false;
}

/** Clientes Training (coach). gym_id ancla D28D no los saca de este dominio. */
export function isTrainingDomainUser(user) {
  if (hasAnyRoleIn(user, PRIVILEGED_ADMIN_ROLES)) return false;
  if (hasAnyRoleIn(user, COACH_STAFF_ROLES)) return false;
  if (hasAnyRoleIn(user, D28D_STAFF_ROLES)) return false;
  if (isFoodOnlyDomainUser(user)) return false;
  return isTrainingOwnedConsumer(user);
}

/** Coaches del ecosistema Training (cuentas operador entrenador/nutricionista). */
export function isTrainingCoachUser(user) {
  return hasAnyRoleIn(user, COACH_STAFF_ROLES);
}

/** Perfil Nest Food (users.food_user_id). */
export function hasFoodNestLink(user) {
  const id = user?.food_user_id ?? user?.foodUserId;
  return id != null && String(id).trim() !== '';
}

export function summarizeFoodRoster(users = []) {
  const list = Array.isArray(users) ? users : [];
  let linked = 0;
  for (const u of list) {
    if (hasFoodNestLink(u)) linked += 1;
  }
  return {
    total: list.length,
    linked,
    unlinked: list.length - linked,
  };
}

export function filterFoodRoster(users, roster = 'all') {
  if (roster === 'linked') return users.filter(hasFoodNestLink);
  if (roster === 'unlinked') return users.filter((u) => !hasFoodNestLink(u));
  return users;
}

/** Usuarios del dominio FOOD: plan/licencia Food, no D28D ni Training. */
export function isFoodDomainUser(user) {
  if (hasAnyRoleIn(user, PRIVILEGED_ADMIN_ROLES)) return false;
  if (hasAnyRoleIn(user, COACH_STAFF_ROLES)) return false;
  if (hasAnyRoleIn(user, D28D_STAFF_ROLES)) return false;
  return isFoodOnlyDomainUser(user);
}

/** Clientes asignados directamente a un entrenador. */
export function isCoachManagedClient(user) {
  if (hasAnyRoleIn(user, PRIVILEGED_ADMIN_ROLES)) return false;
  if (hasAnyRoleIn(user, COACH_STAFF_ROLES)) return false;
  return Boolean(user.trainer_id);
}

export function filterUsersByScope(users, scope) {
  if (!scope || scope === 'default') return users;
  switch (scope) {
    case 'ecosystem-finals':
      return users.filter(isEcosystemFinalUser);
    case 'd28d':
      return users.filter(isD28dDomainUser);
    case 'training-clients':
      return users.filter(isTrainingDomainUser);
    case 'training-coaches':
      return users.filter(isTrainingCoachUser);
    case 'coach-clients':
      return users.filter(isCoachManagedClient);
    case 'food-clients':
      return users.filter(isFoodDomainUser);
    default:
      return users;
  }
}

export function resolveAdminUserScope({
  openServicePanel,
  isCoachActor,
  hasAnyRole,
}) {
  if (openServicePanel === 'training') return 'training-clients';
  if (openServicePanel === 'd28d') return 'd28d';
  if (openServicePanel === 'food-plan') return 'food-clients';
  if (isCoachActor) return 'coach-clients';
  if (hasAnyRole(['admin_training', 'admin_entrenador']) && !hasAnyRole(['super_admin', 'admin_d28d'])) {
    return 'training-clients';
  }
  if (hasAnyRole(['super_admin'])) return 'ecosystem-finals';
  if (hasAnyRole(['admin_d28d']) && openServicePanel !== 'food-plan') return 'd28d';
  return 'default';
}

export const SCOPE_META = {
  'ecosystem-finals': {
    title: 'Ecosistema',
    subtitle: '',
  },
  d28d: {
    title: 'D28D',
    subtitle: 'Usuarios de gimnasios virtuales, staff D28D y clientes del programa.',
  },
  'training-clients': {
    title: 'Clientes Training',
    subtitle: 'Usuarios finales vinculados a un coach (sin gym D28D/sede).',
  },
  'training-coaches': {
    title: 'Coaches',
    subtitle: 'Cuentas operador con rol entrenador o nutricionista.',
  },
  'coach-clients': {
    title: 'Entrenadores',
    subtitle: '',
  },
  'food-clients': {
    title: 'FOOD',
    subtitle: 'Cuentas del producto FOOD PLAN en el ecosistema. «En Food Plan» = mismo universo que Maestros Food (perfil Nest).',
  },
};

export const SUPER_ADMIN_DOMAIN_TABS = [
  { id: 'd28d', label: 'D28D' },
  { id: 'training-coaches', label: 'Coaches' },
  { id: 'training-clients', label: 'Clientes Training' },
  { id: 'food-clients', label: 'FOOD' },
  { id: 'ecosystem-finals', label: 'Ecosistema' },
];

export function getSuperAdminDomainTabs() {
  if (!isFitLiveUiEnabled()) return SUPER_ADMIN_DOMAIN_TABS;
  return SUPER_ADMIN_DOMAIN_TABS.map((tab) => (
    tab.id === 'd28d' ? { ...tab, label: 'Entrenadores Fit Live' } : tab
  ));
}

export function getScopeMeta(key) {
  const meta = SCOPE_META[key];
  if (!meta) return null;
  if (!isFitLiveUiEnabled()) return meta;
  if (key === 'd28d') {
    return {
      title: 'Entrenadores Fit Live',
      subtitle: 'Creators Fit Live: clases en vivo, replays y asistencia.',
    };
  }
  if (key === 'training-clients') {
    return {
      ...meta,
      subtitle: 'Usuarios finales vinculados a un coach (sin gym/sede).',
    };
  }
  return meta;
}
